import { ChartCanvas, Chart } from "@react-financial-charts/core";
import { XAxis, YAxis } from "@react-financial-charts/axes";
import { BarSeries, LineSeries } from "@react-financial-charts/series";
import { CrossHairCursor, MouseCoordinateX, MouseCoordinateY } from "@react-financial-charts/coordinates";
import { format } from "d3-format";
import type { FlowsPointDto } from "../api";
import {
  CHART_HEIGHT, FII_COLOR, DII_COLOR, VIX_UP_COLOR, VIX_DOWN_COLOR,
  useContainerWidth, buildScale, symmetricExtent, NotEnoughPoints,
} from "./flowsChartShared";

/** TAB 4 "Cumulative flows" -- FII net and DII net as running sums over the
 * window (₹Cr), the mock's own "20-day cumulative -- slope matters more
 * than any single day" framing: a persistent upward or downward SLOPE is
 * the net positioning trend, where Tab 1's daily bars are noisy day to day.
 * VIX day-change bars stay as context on the right axis, same as Tab 1, so
 * a viewer can still see which fear spikes coincide with a slope change.
 *
 * IMPORTANT CAVEAT, stated inline (not just in a tooltip): this is a
 * cumulative of what was actually CAPTURED in this window, not a true
 * running total -- the 29 gap days' real flows are unknown and are NOT
 * imputed as zero, and the starting point is this window's own start, not
 * some absolute origin. A slope is still meaningful; the ABSOLUTE level
 * is not "total FII ever."
 */

interface ChartDatum {
  date: Date;
  fii_cum: number | null;
  dii_cum: number | null;
  vix_change: number | null;
}

export function FlowsCumulativeChart({ points }: { points: FlowsPointDto[] }) {
  const { ref, width } = useContainerWidth<HTMLDivElement>();

  const data: ChartDatum[] = points.map((p) => ({
    date: new Date(p.date), fii_cum: p.fii_cum, dii_cum: p.dii_cum, vix_change: p.vix_change,
  }));

  if (data.length < 2 || width === 0) {
    return data.length < 2 ? <NotEnoughPoints containerRef={ref} /> : <div ref={ref} style={{ minHeight: 40 }} />;
  }

  const { plotData, xScale, xAccessor, displayXAccessor, xExtents, formatTickDate } = buildScale(data);
  const cumValues = data.flatMap((d) => [d.fii_cum, d.dii_cum]);
  const cumReal = cumValues.filter((v): v is number => v != null);
  const cumMin = cumReal.length ? Math.min(...cumReal, 0) : 0;
  const cumMax = cumReal.length ? Math.max(...cumReal, 0) : 1;
  const cumPad = Math.max((cumMax - cumMin) * 0.1, 1);
  const cumExtents: [number, number] = [cumMin - cumPad, cumMax + cumPad];

  const vixChangeValues = data.map((d) => d.vix_change);
  const hasVix = vixChangeValues.some((v) => v != null);
  const vixExtents = symmetricExtent(vixChangeValues);

  return (
    <div ref={ref}>
      <div className="flows-legend">
        <span><i style={{ background: FII_COLOR }} /> FII net, cumulative (₹cr)</span>
        <span><i style={{ background: DII_COLOR }} /> DII net, cumulative (₹cr)</span>
        {hasVix ? (
          <span>
            <i style={{ background: VIX_UP_COLOR }} />/<i style={{ background: VIX_DOWN_COLOR }} /> VIX day-change
            (right axis, context)
          </span>
        ) : null}
      </div>
      <ChartCanvas
        height={CHART_HEIGHT}
        width={width}
        ratio={window.devicePixelRatio || 1}
        margin={{ left: 60, right: hasVix ? 48 : 8, top: 8, bottom: 24 }}
        data={plotData}
        seriesName="flows-cumulative"
        xScale={xScale}
        xAccessor={xAccessor}
        displayXAccessor={displayXAccessor}
        xExtents={xExtents}
      >
        <Chart id={0} height={CHART_HEIGHT} yExtents={cumExtents} padding={{ top: 10, bottom: 10 }}>
          <XAxis showGridLines tickFormat={formatTickDate("%d %b")} />
          <YAxis showGridLines tickFormat={format(".2s")} />
          <LineSeries yAccessor={(d: ChartDatum) => d.fii_cum ?? undefined} strokeStyle={FII_COLOR} strokeWidth={1.6} />
          <LineSeries yAccessor={(d: ChartDatum) => d.dii_cum ?? undefined} strokeStyle={DII_COLOR} strokeWidth={1.6} />
          <MouseCoordinateY displayFormat={format(".2f")} />
          <MouseCoordinateX displayFormat={formatTickDate("%d %b %Y")} />
        </Chart>
        {hasVix ? (
          <Chart id={1} height={CHART_HEIGHT} yExtents={vixExtents} padding={{ top: 10, bottom: 10 }}>
            <YAxis axisAt="right" orient="right" showGridLines={false} tickFormat={format("+.1f")} />
            <BarSeries
              yAccessor={(d: ChartDatum) => d.vix_change ?? undefined}
              baseAt={(_xScale, yScale) => yScale(0)}
              fillStyle={(d: ChartDatum) => (d.vix_change != null && d.vix_change >= 0 ? VIX_UP_COLOR : VIX_DOWN_COLOR)}
              widthRatio={0.4}
            />
          </Chart>
        ) : null}
        <CrossHairCursor />
      </ChartCanvas>
    </div>
  );
}
